
import { defineComponent, PropType } from 'vue'

import ActiveUserMixin from '~/mixins/activeUserMixin'
import AutofixRunMixin from '~/mixins/autofixRunMixin'
import RepoDetailMixin from '~/mixins/repoDetailMixin'

import CreateAutofixRun from '~/apollo/mutations/issue/createAutofixRun.gql'

// types
import { AutofixListArgs } from '~/types/autofix'
import { CreateAutofixRunInput, CreateAutofixRunPayload } from '~/types/types'

export default defineComponent({
  name: 'AutofixFileChooser',
  mixins: [ActiveUserMixin, RepoDetailMixin, AutofixRunMixin],
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    raisedInFiles: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    repoParams: {
      type: Object as PropType<{ name: string; provider: string; login: string }>,
      required: false
    },
    issueId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      searchCandidate: '',
      selectedValue: '',
      allFiles: [] as { fileName: string; isSelected: boolean }[],
      selectAll: true,
      maxFilesAutofixRun: 50,
      autofixLoading: false
    }
  },
  emits: ['close', 'run-quota-exhausted'],
  mounted() {
    this.selectAll = true
  },
  watch: {
    raisedInFiles: {
      immediate: true,
      handler(newRaisedInFiles: Array<string>): void {
        this.updateFiles(newRaisedInFiles)
      }
    },
    selectAll(): void {
      this.updateSelectAll()
    }
  },
  computed: {
    searchResults(): { fileName: string; isSelected: boolean }[] {
      if (this.searchCandidate) {
        return this.allFiles.filter((file) => {
          return file.fileName.toLowerCase().includes(this.searchCandidate.toLowerCase())
        })
      }

      return this.allFiles
    },
    selectedFiles(): string[] {
      return this.allFiles.filter((file) => file.isSelected).map((file) => file.fileName)
    }
  },
  methods: {
    updateFiles(newRaisedInFiles: Array<string>): void {
      this.allFiles = newRaisedInFiles.map((file) => {
        return {
          fileName: file,
          isSelected: this.selectAll
        }
      })
    },
    updateSelectAll(): void {
      const searchedFiles = this.searchResults.map((file) => file.fileName)
      this.allFiles.forEach((file) => {
        if (searchedFiles.includes(file.fileName)) {
          file.isSelected = this.selectAll
        }
      })
    },
    close(): void {
      this.$emit('close')
    },
    async autofixSelectedFiles(close: () => void): Promise<void> {
      this.autofixLoading = true
      try {
        const response = await this.createAutofixRun({
          inputFiles: this.selectedFiles,
          repoIssueId: this.issueId
        })

        if (this.$route.name?.startsWith('provider-owner-repo')) {
          this.$router.push(this.$generateRoute(['autofix', response.runId as string]))
        } else if (this.repoParams) {
          const { provider, login, name } = this.repoParams
          this.$router.push(
            ['', provider, login, name, 'autofix', response.runId as string].join('/')
          )
        }

        if (close) {
          close()
        } else {
          this.close()
        }
      } catch (e) {
        const err = e as Error
        if (err.message.includes('Your autofix run quota has been exhausted')) {
          this.$emit('run-quota-exhausted')
        } else {
          this.logErrorForUser(
            err,
            'AutofixError',
            {
              method: 'Create Autofix Run',
              inputFiles: this.selectedFiles,
              repoIssueId: this.issueId
            },
            `${err.message.replace('GraphQL error: ', '')}.` as `${string}.`
          )
        }
      } finally {
        this.autofixLoading = false
      }
    },

    async createAutofixRun(args: CreateAutofixRunInput): Promise<CreateAutofixRunPayload> {
      let provider = ''
      let owner = ''
      let name = ''

      if (this.$route.name?.startsWith('provider-owner-repo')) {
        const { provider: routeProvider, owner: routeOwner, repo: routeName } = this.$route.params
        provider = routeProvider
        owner = routeOwner
        name = routeName
      } else if (this.repoParams) {
        const { provider: paramProvider, login: paramOwner, name: paramName } = this.repoParams
        provider = paramProvider
        owner = paramOwner
        name = paramName
      }

      const response = await this.$applyGraphqlMutation(CreateAutofixRun, {
        input: args
      })

      const autofixListQueryArgs: AutofixListArgs = {
        provider,
        owner,
        name,
        currentPageNumber: 1,
        limit: 30,
        q: '',
        refetch: true
      }

      //? Trigger autofix run list refetch
      // No need to await as it can be async
      this.fetchAutofixHistoryRunList(autofixListQueryArgs)
      this.fetchPendingAutofixRunList(autofixListQueryArgs)

      return response.data.createAutofixRun
    }
  }
})
